const parseHTML = (markup) => new DOMParser().parseFromString(markup, 'text/html');
const shopRoot = () => window.Shopify?.routes?.root || '/';

class ProductInfo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.onChange = this.onChange.bind(this);
    this.onOutsidePointerDown = this.onOutsidePointerDown.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('pointerdown', this.onOutsidePointerDown);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown);
    this.controller?.abort();
    this.initialized = false;
  }

  onOutsidePointerDown(event) {
    const tooltip = this.querySelector('.product-service-tooltip[open]');
    if (tooltip && !tooltip.contains(event.target)) tooltip.open = false;
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    const tooltip = this.querySelector('.product-service-tooltip[open]');
    if (!tooltip) return;
    tooltip.open = false;
    tooltip.querySelector('summary')?.focus();
  }

  async onChange(event) {
    const option = event.target.closest('[data-option-value-id]');
    if (!option) return;

    const selectors = option.closest('[data-option-value-selectors]');
    const optionValueIds = [...selectors.querySelectorAll('[data-option-value-id]:checked')].map((input) => input.dataset.optionValueId);
    const productUrl = option.dataset.productUrl || this.dataset.productUrl;
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    if (optionValueIds.length) url.searchParams.set('option_values', optionValueIds.join(','));

    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Product section returned ${response.status}`);
      const refreshed = parseHTML(await response.text()).querySelector('product-info');
      if (!refreshed) throw new Error('Product section markup was missing');

      const focusSelector = `[data-option-value-id="${CSS.escape(option.dataset.optionValueId)}"]`;
      const publicUrl = new URL(productUrl, window.location.origin);
      publicUrl.searchParams.set('option_values', optionValueIds.join(','));
      window.history.replaceState({}, '', publicUrl);
      this.replaceWith(refreshed);
      refreshed.dispatchEvent(new CustomEvent('velora:content:loaded', { bubbles: true }));
      refreshed.querySelector(focusSelector)?.focus();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.removeAttribute('aria-busy');
      this.querySelector('[data-product-form-message]')?.removeAttribute('hidden');
      const message = this.querySelector('[data-product-form-message]');
      if (message) message.textContent = window.veloraStrings?.optionLoadError || 'This option could not be loaded. Please try again.';
    }
  }
}

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

class QuantitySelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.addEventListener('click', this.onClick);
    this.input.addEventListener('input', this.onInput);
    this.initialized = true;
    this.syncButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.input?.removeEventListener('input', this.onInput);
    this.initialized = false;
  }

  onClick(event) {
    const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!button || button.disabled) return;
    const minimum = Number(this.input.min || 1);
    const maximum = this.input.max === '' ? Infinity : Number(this.input.max);
    const increment = Number(this.input.step || 1);
    const current = Number(this.input.value || minimum);
    const direction = button.hasAttribute('data-quantity-plus') ? 1 : -1;
    const precision = (String(increment).split('.')[1] || '').length;
    const next = Math.min(maximum, Math.max(minimum, current + (increment * direction)));
    this.input.value = String(Number(next.toFixed(precision)));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncButtons();
  }

  onInput() {
    this.syncButtons();
  }

  syncButtons() {
    const value = Number(this.input.value || this.input.min || 1);
    const minimum = Number(this.input.min || 1);
    const maximum = this.input.max === '' ? Infinity : Number(this.input.max);
    const minus = this.querySelector('[data-quantity-minus]');
    const plus = this.querySelector('[data-quantity-plus]');
    if (minus) minus.disabled = value <= minimum;
    if (plus) plus.disabled = value >= maximum;
  }
}

if (!customElements.get('quantity-selector')) customElements.define('quantity-selector', QuantitySelector);

class SellingPlanSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.initialized = false;
  }

  onChange(event) {
    const option = event.target.closest('[data-selling-plan-option]');
    if (!option) return;
    const form = this.closest('form');
    const input = this.querySelector('[data-selling-plan-input]');
    if (input) input.value = option.value;

    const currentPrice = form?.querySelector('[data-current-price]');
    const comparePrice = form?.querySelector('[data-compare-price]');
    if (currentPrice && option.dataset.price) currentPrice.textContent = option.dataset.price;
    if (comparePrice) {
      comparePrice.textContent = option.dataset.comparePrice || '';
      comparePrice.hidden = !option.dataset.comparePrice;
    }
  }
}

if (!customElements.get('selling-plan-selector')) customElements.define('selling-plan-selector', SellingPlanSelector);

class ProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.form = this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;
    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.initialized = false;
  }

  async onSubmit(event) {
    const behavior = this.form.dataset.cartBehavior || 'page';
    if (behavior === 'page') return;
    event.preventDefault();

    const button = event.submitter || this.form.querySelector('[data-primary-add-to-cart]') || this.form.querySelector('[data-add-to-cart]');
    const buttons = [...this.form.querySelectorAll('[data-add-to-cart]')];
    const buttonStates = buttons.map((candidate) => [candidate, candidate.disabled]);
    const message = this.closest('product-info')?.querySelector('[data-product-form-message]') || this.querySelector('[data-product-form-message]');
    buttons.forEach((candidate) => {
      candidate.disabled = true;
      candidate.setAttribute('aria-disabled', 'true');
    });
    this.form.setAttribute('aria-busy', 'true');
    if (message) message.hidden = true;

    const formData = new FormData(this.form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || window.veloraStrings?.cartAddError || 'The item could not be added.');

      const drawer = document.querySelector('cart-drawer');
      if (result.sections?.['cart-drawer']) drawer?.render(result.sections['cart-drawer']);
      if (behavior === 'drawer') drawer?.open(button);
      const itemAdded = window.veloraStrings?.itemAdded || '__TITLE__ added to your cart.';
      this.announce(message, itemAdded.replace('__TITLE__', result.product_title || 'Item'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));
    } catch (error) {
      this.announce(message, error.message);
    } finally {
      buttonStates.forEach(([candidate, wasDisabled]) => {
        candidate.disabled = wasDisabled;
        candidate.removeAttribute('aria-disabled');
      });
      this.form.removeAttribute('aria-busy');
    }
  }

  announce(localMessage, text) {
    if (localMessage) {
      localMessage.textContent = text;
      localMessage.hidden = false;
    }
    const globalRegion = document.querySelector('[data-live-region]');
    if (globalRegion) globalRegion.textContent = text;
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

class FeaturedVariantSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.select = this.querySelector('[data-featured-variant]');
    if (!this.select) return;
    this.onChange = this.onChange.bind(this);
    this.select.addEventListener('change', this.onChange);
    this.initialized = true;
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.onChange);
    this.initialized = false;
  }

  onChange() {
    const option = this.select.selectedOptions[0];
    const section = this.closest('[data-featured-spirit]');
    if (!option || !section) return;

    const available = option.dataset.available === 'true';
    const button = this.querySelector('[data-add-to-cart]');
    const buttonText = button?.querySelector('[data-add-to-cart-text]');
    const price = section.querySelector('[data-featured-price]');
    const currentPrice = price?.querySelector('[data-featured-current]');
    const comparePrice = price?.querySelector('[data-featured-compare]');
    const regularLabel = price?.querySelector('[data-featured-regular-label]');
    const currentLabel = price?.querySelector('[data-featured-current-label]');
    const hasComparePrice = Boolean(option.dataset.compare);

    if (button) button.disabled = !available;
    if (buttonText) buttonText.textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
    if (currentPrice) currentPrice.textContent = option.dataset.price;
    if (comparePrice) {
      comparePrice.textContent = option.dataset.compare || '';
      comparePrice.hidden = !hasComparePrice;
    }
    if (regularLabel) regularLabel.hidden = !hasComparePrice;
    if (currentLabel) currentLabel.textContent = hasComparePrice ? price.dataset.saleLabel : price.dataset.priceLabel;
    price?.classList.toggle('price--sale', hasComparePrice);

    const productLink = section.querySelector('[data-featured-product-link]');
    if (productLink && option.dataset.url) productLink.href = option.dataset.url;
  }
}

if (!customElements.get('featured-variant-selector')) customElements.define('featured-variant-selector', FeaturedVariantSelector);

class CartDrawer extends HTMLElement {
  render(markup) {
    const refreshed = parseHTML(markup).querySelector('cart-drawer');
    if (!refreshed) return;
    this.replaceChildren(...refreshed.childNodes);
    const count = this.querySelector('[data-cart-drawer-count]')?.textContent?.trim();
    if (count !== undefined) document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = count; });
  }

  open(opener) {
    const dialog = this.querySelector('dialog');
    if (!dialog || dialog.open) return;
    this.opener = opener;
    dialog.showModal();
    dialog.querySelector('[data-dialog-close]')?.focus();
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class PickupAvailability extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.variantId) return;
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async load() {
    this.controller = new AbortController();
    const root = this.dataset.rootUrl || shopRoot();
    try {
      const response = await fetch(`${root}variants/${this.dataset.variantId}/?section_id=pickup-availability`, { signal: this.controller.signal });
      if (!response.ok) return;
      const section = parseHTML(await response.text()).querySelector('.shopify-section');
      const target = this.querySelector('[data-pickup-content]');
      if (target && section) target.replaceChildren(...section.childNodes);
    } catch (error) {
      if (error.name !== 'AbortError') this.replaceChildren();
    }
  }
}

if (!customElements.get('pickup-availability')) customElements.define('pickup-availability', PickupAvailability);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.initialized || !this.dataset.url) return;
    this.initialized = true;
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.initialized = false;
  }

  async load() {
    this.controller = new AbortController();
    try {
      const response = await fetch(this.dataset.url, { signal: this.controller.signal });
      if (!response.ok) return;
      const refreshed = parseHTML(await response.text()).querySelector('product-recommendations');
      if (refreshed?.innerHTML.trim()) {
        this.replaceChildren(...refreshed.childNodes);
        this.dispatchEvent(new CustomEvent('velora:content:loaded', { bubbles: true }));
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.hidden = true;
    }
  }
}

if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

const refreshStandardCartUI = async () => {
  const response = await fetch(`${shopRoot()}cart.js?sections=cart-drawer,main-cart`, {
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok) throw new Error(`Cart sections returned ${response.status}`);
  const cart = await response.json();
  const drawer = document.querySelector('cart-drawer');
  if (cart.sections?.['cart-drawer']) drawer?.render(cart.sections['cart-drawer']);

  const currentMainCart = document.querySelector('.main-cart')?.closest('.shopify-section');
  const refreshedMainCart = cart.sections?.['main-cart'] ? parseHTML(cart.sections['main-cart']).querySelector('.shopify-section') : null;
  if (currentMainCart && refreshedMainCart) currentMainCart.replaceChildren(...refreshedMainCart.childNodes);
};

const configureStandardCartActions = () => {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const ageDialog = document.querySelector('age-gate dialog[open]');
      if (ageDialog) {
        ageDialog.focus();
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return defaultHandler();
      await refreshStandardCartUI();
      drawer.open(document.activeElement instanceof HTMLElement ? document.activeElement : undefined);
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshStandardCartUI();
      } catch (error) {
        console.error('[Velora] Standard cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
};

if (window.Shopify?.actions) configureStandardCartActions();
else document.addEventListener('DOMContentLoaded', configureStandardCartActions, { once: true });
